import { abbrToState } from "shared/app/VerificationTemplate/slices/CountyWalkInTypeSlice/constants";
import { EnrollmentOption, EnrollmentSource } from "types/landlord/enrollment";
import {
  AddressCounts,
  AlgoliaUser,
  EnrollmentSelectValue,
  PartnerDefaultEnrollments,
  PmsLeaseRow,
  RentersFilters
} from "views/renters/types";
import { TabType } from "views/renters/components/Header/types";

export type EnrollmentDisplayItem = { id: string; label: string };

export const LEASES_PAGE_SIZE = 10;

export const RENTER_STATUS_OPTIONS = [
  { value: TabType.NotSubscribed, label: "Not enrolled" },
  { value: TabType.Failed, label: "Failed to enroll" },
  { value: TabType.Subscribed, label: "Enrolled" }
];

export const RENTERS_V2_TABS: TabType[] = [
  TabType.NotSubscribed,
  TabType.Failed,
  TabType.Subscribed,
  TabType.All
];

export const getSubscriptionStatusLabel = (
  enrollmentStatus: string | undefined
): string => {
  switch (enrollmentStatus) {
    case "enrolled":
      return "Active";
    case "not_enrolled":
      return "Not enrolled";
    case "error":
      return "Failed to enroll";
    default:
      return enrollmentStatus || "--";
  }
};

const normalizeAddress = (s: string): string =>
  s.trim().toLowerCase().replace(/\s+/g, " ");

export const verboseEnrollmentType = (
  enrollmentType: string,
  lowercase: boolean = false
): string => {
  let verboseEnrollmentType = enrollmentType;
  if (enrollmentType === "direct") verboseEnrollmentType = "Required";
  if (enrollmentType === "opt_in") verboseEnrollmentType = "Opt-in";
  if (enrollmentType === "opt_out") verboseEnrollmentType = "Opt-out";
  if (enrollmentType === "default") verboseEnrollmentType = "Default";

  if (lowercase) return verboseEnrollmentType.toLowerCase();

  return verboseEnrollmentType;
};

export const prepareAlgoliaData = (
  data: AlgoliaUser[],
  enrollmentOption: EnrollmentOption
) => {
  return data.map((renter) => {
    const enrollmentSource: EnrollmentSource = "algolia";
    const { units, manager, phone, email, ...property } = renter.property;
    const { boomCustomerId, pets, hasPets, ...customer } = renter.customer;
    const {
      _highlightResult,
      boomEnrollmentStatus,
      property: _property,
      customer: _customer,
      ...renterDetails
    } = renter;

    const ret = {
      ...renterDetails,
      property,
      customer: customer,
      opt_out: enrollmentOption.optOut,
      opt_in: enrollmentOption.optIn,
      is_default_enrollment: enrollmentOption.isDefaultEnrollment,
      from_source: enrollmentSource
    };

    return ret;
  });
};

type OverrideEntry = {
  default_enrollment_type: string;
  property_name: string | null;
  address1: string;
};

const buildOverridesByAddress = (
  propertyDefaults: Array<{
    property_id: string;
    default_enrollment_type: string;
    property_name: string | null;
    address1?: string | null;
  }>
): Map<string, OverrideEntry> => {
  const map = new Map<string, OverrideEntry>();
  for (const p of propertyDefaults)
    if (p.address1) {
      const key = normalizeAddress(p.address1);
      map.set(key, {
        default_enrollment_type: p.default_enrollment_type,
        property_name: p.property_name,
        address1: p.address1
      });
    }
  return map;
};

/**
 * Calculates the count of renters by address or state from an array of renters.
 * Match by address1 first: if the renter's property address1 matches a property_defaults
 * entry, use that enrollment. Otherwise fall back to state-based defaults.
 */
export const groupedDefaultEnrollmentTypes = ({
  rentersToProcess,
  partnerDefaultEnrollmentType,
  defaultEnrollmentTypes,
  propertyDefaults = []
}: {
  rentersToProcess: AlgoliaUser[];
  partnerDefaultEnrollmentType: string | undefined;
  defaultEnrollmentTypes: Array<{
    state_province: string;
    default_enrollment_type: string;
  }>;
  propertyDefaults?: Array<{
    property_id: string;
    default_enrollment_type: string;
    property_name: string | null;
    address1?: string | null;
  }>;
}): EnrollmentDisplayItem[] => {
  const overridesByAddress = buildOverridesByAddress(propertyDefaults);
  const hasPartnerOrStateDefaults =
    !!partnerDefaultEnrollmentType || defaultEnrollmentTypes.length > 0;
  const hasPropertyDefaults = overridesByAddress.size > 0;
  if (!hasPartnerOrStateDefaults && !hasPropertyDefaults) return [];

  const stateCounts: Record<string, number> = {};
  const addressCounts: Record<string, number> = {};
  const addressMeta: Record<
    string,
    { name: string; default_enrollment_type: string }
  > = {};
  const statesWithDefaultEnrollmentType = new Set<string>();
  const optValueByState: Record<string, string> = {};

  defaultEnrollmentTypes.forEach((type) => {
    statesWithDefaultEnrollmentType.add(type.state_province);
    optValueByState[type.state_province] = type.default_enrollment_type;
  });

  const rentersWithoutOverride: AlgoliaUser[] = [];

  rentersToProcess.forEach((renter) => {
    const addr = renter.property.address1.trim();
    const key = addr ? normalizeAddress(addr) : "";
    const override = key ? overridesByAddress.get(key) : undefined;
    if (override) {
      addressCounts[key] = (addressCounts[key] || 0) + 1;
      addressMeta[key] = {
        name:
          override.property_name || renter.property.name || override.address1,
        default_enrollment_type: override.default_enrollment_type
      };
    } else rentersWithoutOverride.push(renter);
  });

  const result: EnrollmentDisplayItem[] = [];

  if (Object.keys(addressCounts).length > 0)
    for (const [key, count] of Object.entries(addressCounts)) {
      const { name, default_enrollment_type } = addressMeta[key];
      result.push({
        id: `address-${key}`,
        label: `${name}: ${count} ${verboseEnrollmentType(default_enrollment_type, true)}`
      });
    }

  if (rentersWithoutOverride.length === 0) return result;
  if (!partnerDefaultEnrollmentType) return result;

  rentersWithoutOverride.forEach((renter) => {
    const state = renter.property.state;
    if (statesWithDefaultEnrollmentType.has(state))
      stateCounts[state] = (stateCounts[state] || 0) + 1;
    else stateCounts["Other"] = (stateCounts["Other"] || 0) + 1;
  });

  const entries = Object.entries(stateCounts);
  const isSingleUnspecifiedState =
    entries.length === 1 && entries[0][0] === "Other";
  if (isSingleUnspecifiedState && !partnerDefaultEnrollmentType) return result;
  if (isSingleUnspecifiedState && partnerDefaultEnrollmentType) {
    result.push({
      id: "state-Other",
      label: `${stateCounts["Other"]} ${verboseEnrollmentType(partnerDefaultEnrollmentType)}`
    });
    return result;
  }

  for (const [state, count] of entries) {
    const id = `state-${state}`;
    const label =
      state === "Other"
        ? `Other: ${count} ${verboseEnrollmentType(partnerDefaultEnrollmentType || "", true)}`
        : `${abbrToState[state]}: ${count} ${verboseEnrollmentType(optValueByState[state] || "", true)}`;
    result.push({ id, label });
  }
  return result;
};

/**
 * Determines the enrollment type for a single renter. Match by address1 first:
 * if the property's address1 matches a property_defaults entry, use that enrollment;
 * otherwise fall back to state-based defaults.
 */
export const getRenterEnrollmentType = ({
  state,
  address1,
  propertyName,
  defaultEnrollmentTypes,
  partnerDefaultEnrollmentType,
  propertyDefaults = []
}: {
  state: string;
  address1?: string | null;
  propertyName?: string;
  defaultEnrollmentTypes: Array<{
    state_province: string;
    default_enrollment_type: string;
  }>;
  partnerDefaultEnrollmentType: string | undefined;
  propertyDefaults?: Array<{
    property_id: string;
    default_enrollment_type: string;
    property_name: string | null;
    address1?: string | null;
  }>;
}): EnrollmentDisplayItem[] => {
  const key = address1?.trim() ? normalizeAddress(address1) : "";
  const propertyOverride = key
    ? propertyDefaults.find(
        (p) => p.address1 && normalizeAddress(p.address1) === key
      )
    : undefined;
  if (propertyOverride) {
    const name =
      propertyOverride.property_name ||
      propertyName ||
      propertyOverride.address1 ||
      "Property";
    return [
      {
        id: `address-${key}`,
        label: `${name}: ${verboseEnrollmentType(propertyOverride.default_enrollment_type)}`
      }
    ];
  }
  if (!partnerDefaultEnrollmentType) return [];

  const stateEnrollmentType = defaultEnrollmentTypes.find(
    (type) => type.state_province === state
  );

  if (stateEnrollmentType)
    return [
      {
        id: `state-${state}`,
        label: `${abbrToState[state]}: ${verboseEnrollmentType(stateEnrollmentType.default_enrollment_type)}`
      }
    ];

  if (partnerDefaultEnrollmentType)
    return [
      {
        id: "partner-default",
        label: verboseEnrollmentType(partnerDefaultEnrollmentType)
      }
    ];

  return [];
};

export const getEnrollmentsByStateCopy = (
  enrollmentsByState: Record<string, number>,
  partnerDefaultEnrollmentType: string | undefined,
  defaultEnrollmentTypes: Array<{
    state_province: string;
    default_enrollment_type: string;
  }>
) => {
  if (!partnerDefaultEnrollmentType) return "";
  if (Object.keys(enrollmentsByState).length === 0) return "";

  const statesWithDefaultEnrollmentType = new Set<string>();
  const optValueByState: Record<string, string> = {};

  defaultEnrollmentTypes.forEach((type) => {
    statesWithDefaultEnrollmentType.add(type.state_province);
    optValueByState[type.state_province] = type.default_enrollment_type;
  });

  const entries = Object.entries(enrollmentsByState);
  const totalUnscopedCount = entries.reduce(
    (acc, [state, count]) =>
      statesWithDefaultEnrollmentType.has(state) ? acc : acc + count,
    0
  );

  const isSingleUnspecifiedState =
    entries.length === 1 && !statesWithDefaultEnrollmentType.has(entries[0][0]);
  if (isSingleUnspecifiedState && !partnerDefaultEnrollmentType) return "";

  if (isSingleUnspecifiedState && partnerDefaultEnrollmentType)
    return `${totalUnscopedCount} ${verboseEnrollmentType(partnerDefaultEnrollmentType)}`;

  const filteredEntries = entries.filter(([state, count]) =>
    statesWithDefaultEnrollmentType.has(state)
  );
  const mappedEntries = filteredEntries.map(
    ([state, count]) =>
      `${abbrToState[state]}: ${count} ${verboseEnrollmentType(optValueByState[state] || "", true)}`
  );
  if (totalUnscopedCount > 0 && partnerDefaultEnrollmentType)
    mappedEntries.push(
      `Other: ${totalUnscopedCount} ${verboseEnrollmentType(partnerDefaultEnrollmentType || "", true)}`
    );

  return mappedEntries.join(", ");
};

// --- RentersPageV2 helpers ---

export function getRenterCountLabel(
  enrollmentTab: TabType,
  totalCount: number | null
): string {
  const count = totalCount ?? 0;
  if (enrollmentTab === TabType.Failed)
    return `${count} renters failed to enroll`;
  if (enrollmentTab === TabType.NotSubscribed)
    return `${count} renters not enrolled`;
  if (enrollmentTab === TabType.Subscribed) return `${count} renters enrolled`;
  return `${count} total renters`;
}

export function getLabelsForStatusValues(values: string[]): string[] {
  return values.map(
    (value) =>
      RENTER_STATUS_OPTIONS.find((opt) => opt.value === value)?.label ?? value
  );
}

export function getValuesForStatusLabels(labels: string[]): string[] {
  return labels
    .map(
      (label) =>
        RENTER_STATUS_OPTIONS.find((opt) => opt.label === label)?.value as
          | string
          | undefined
    )
    .filter((v): v is string => v !== undefined);
}

export function enrollmentOptionToSelectValue(
  opt: EnrollmentOption
): EnrollmentSelectValue {
  if (opt.isDefaultEnrollment) return "isDefaultEnrollment";
  if (opt.optIn) return "optIn";
  if (opt.optOut) return "optOut";
  return "optInNow";
}

export const ENROLLMENT_TYPE_OPTIONS: {
  label: string;
  value: EnrollmentSelectValue;
}[] = [
  { label: "Default enrollment type", value: "isDefaultEnrollment" },
  { label: "Renters can opt in", value: "optIn" },
  { label: "Renters can opt out", value: "optOut" },
  { label: "Required", value: "optInNow" }
];

export const ENROLLMENT_TYPE_OPTIONS_NO_DEFAULT =
  ENROLLMENT_TYPE_OPTIONS.filter((o) => o.value !== "isDefaultEnrollment");

function buildOverridesByAddressFromPartnerDefaults(
  propDefaults: PartnerDefaultEnrollments["property_defaults"]
): Map<string, { type: string; name: string }> {
  const map = new Map<string, { type: string; name: string }>();
  const defaults = propDefaults ?? [];
  for (const p of defaults)
    if (p.address1) {
      const key = p.address1.trim().toLowerCase().replace(/\s+/g, " ");
      map.set(key, {
        type: p.default_enrollment_type,
        name: p.property_name || p.address1
      });
    }
  return map;
}

function groupPendingRowsByAddressAndState(
  pendingRows: PmsLeaseRow[],
  overridesByAddress: Map<string, { type: string; name: string }>
): { addressCounts: AddressCounts; stateCounts: Record<string, number> } {
  const addressCounts: AddressCounts = {};
  const stateCounts: Record<string, number> = {};
  for (const row of pendingRows) {
    const addr = row.unit?.address?.trim() ?? "";
    const key = addr ? addr.toLowerCase().replace(/\s+/g, " ") : "";
    const override = key ? overridesByAddress.get(key) : undefined;
    if (override) {
      if (!(key in addressCounts))
        addressCounts[key] = {
          count: 0,
          name: override.name,
          type: override.type
        };
      addressCounts[key].count++;
    } else {
      const state = row.unit?.state || "Other";
      stateCounts[state] = (stateCounts[state] || 0) + 1;
    }
  }
  return { addressCounts, stateCounts };
}

function buildEnrollmentDisplayItems(
  addressCounts: AddressCounts,
  stateCounts: Record<string, number>,
  stateDefaults: { state_province: string; default_enrollment_type: string }[],
  partnerDefault: string | undefined
): EnrollmentDisplayItem[] {
  const optByState: Record<string, string> = {};
  for (const s of stateDefaults)
    optByState[s.state_province] = s.default_enrollment_type;

  const result: EnrollmentDisplayItem[] = [];
  for (const [key, { count, name, type }] of Object.entries(addressCounts))
    result.push({
      id: `address-${key}`,
      label: `${name}: ${count} ${verboseEnrollmentType(type, true)}`
    });

  const stateEntries = Object.entries(stateCounts);
  if (stateEntries.length === 0 || !partnerDefault) return result;

  for (const [state, count] of stateEntries) {
    const type = optByState[state] || partnerDefault;
    const label =
      state === "Other"
        ? `Other: ${count} ${verboseEnrollmentType(type, true)}`
        : `${abbrToState[state] || state}: ${count} ${verboseEnrollmentType(type, true)}`;
    result.push({ id: `state-${state}`, label });
  }
  return result;
}

export function computePendingEnrollmentDisplay(
  pendingEnrollIds: string[] | null,
  showDefaultEnrollmentType: boolean,
  leaseRows: PmsLeaseRow[],
  partnerDefaultEnrollments: PartnerDefaultEnrollments | undefined
): EnrollmentDisplayItem[] {
  if (!pendingEnrollIds || !showDefaultEnrollmentType) return [];
  const pendingRows = leaseRows.filter((r) => pendingEnrollIds.includes(r.id));
  if (pendingRows.length === 0) return [];

  const stateDefaults =
    partnerDefaultEnrollments?.state_province_defaults ?? [];
  const overridesByAddress = buildOverridesByAddressFromPartnerDefaults(
    partnerDefaultEnrollments?.property_defaults
  );
  const { addressCounts, stateCounts } = groupPendingRowsByAddressAndState(
    pendingRows,
    overridesByAddress
  );
  return buildEnrollmentDisplayItems(
    addressCounts,
    stateCounts,
    stateDefaults,
    partnerDefaultEnrollments?.default_enrollment_type
  );
}

export function getActiveFiltersCount(filters: RentersFilters): number {
  let count = 0;
  if (filters.selectedProperties.length > 0) count++;
  if (filters.selectedStates.length > 0) count++;
  if (filters.selectedRenterStatuses.length > 0) count++;
  if (filters.leaseStartDateFrom) count++;
  if (filters.leaseStartDateTo) count++;
  if (filters.moveInDateFrom) count++;
  if (filters.moveInDateTo) count++;
  if (filters.moveOutDateFrom) count++;
  if (filters.moveOutDateTo) count++;
  return count;
}

export function getConsentSubtext(
  enrollmentOption: EnrollmentOption,
  optInPeriod: {
    unexpiredOptIn: boolean;
    optInDays: number;
    optOutDays: number;
  }
): string | null {
  if (enrollmentOption.isDefaultEnrollment) return null;
  if (enrollmentOption.optIn)
    return optInPeriod.unexpiredOptIn
      ? "Renters are notified via email and can opt in at anytime."
      : `Renters are notified via email and have ${optInPeriod.optInDays} days to opt in.`;
  if (enrollmentOption.optOut)
    return `Renters are notified via email and have ${optInPeriod.optOutDays} days to opt out.`;
  return "Renters are enrolled with no option to opt in or out.";
}
